import { useMemo } from 'react'
import { useAppStore } from '../../app/hooks/useAppStore'
import { getWeakestFamiliesGlobal } from '../../mastery'
import {
  isFluencyColdStart as checkFluencyColdStart,
  type ModeId,
} from '../../modes'
import { humanizeFamilyId } from '../../content'
import type { PersonalRecords } from '../../data/dataLayer'

export interface ModeEnrichment {
  statLabel?: string
  masteryPercent?: number
  personalRecord?: string
}

interface EnrichmentContext {
  fluencyCount: number
  fluencyMastery: number
  challengeCount: number
  challengeMastery: number
  weakestFamilyData: { familyId: string; masteryScore: number } | null
  overallMastery: number
  personalRecords: PersonalRecords
}

type ModeEnrichmentFn = (ctx: EnrichmentContext) => ModeEnrichment

/**
 * MODE_ENRICHMENT — Per-mode strategy for the `LabStation`'s contextual
 * stats (statLabel, masteryPercent, personalRecord).
 *
 * Replaces the old switch-on-modeId pattern (F-CODE-7). Adding a new mode
 * means adding one entry here — TypeScript will surface missing modes via
 * the `Record<ModeId, ...>` constraint once `experiments` is enabled.
 */
const MODE_ENRICHMENT: Partial<Record<ModeId, ModeEnrichmentFn>> = {
  fluency: ({ fluencyCount, fluencyMastery, personalRecords }) => ({
    statLabel:
      fluencyCount > 0
        ? `${fluencyCount} onderwerpen klaar`
        : 'Train je snelheid',
    masteryPercent: fluencyCount > 0 ? fluencyMastery : undefined,
    personalRecord: personalRecords.fluency?.longestStreak
      ? `Record: ${personalRecords.fluency.longestStreak} op rij`
      : undefined,
  }),
  sprint: ({ weakestFamilyData, personalRecords }) => ({
    statLabel: weakestFamilyData
      ? `${humanizeFamilyId(weakestFamilyData.familyId)}`
      : 'Oefen je zwakste onderwerpen',
    masteryPercent: weakestFamilyData
      ? weakestFamilyData.masteryScore
      : undefined,
    personalRecord: personalRecords.sprint?.bestScore
      ? `Beste score: ${personalRecords.sprint.bestScore}%`
      : undefined,
  }),
  challenge: ({ challengeCount, challengeMastery, personalRecords }) => ({
    statLabel:
      challengeCount > 0
        ? `${challengeCount} onderwerpen klaar`
        : 'Test je kennis',
    masteryPercent: challengeCount > 0 ? challengeMastery : undefined,
    personalRecord: personalRecords.challenge?.bestScore
      ? `Beste score: ${personalRecords.challenge.bestScore}%`
      : undefined,
  }),
  free: ({ overallMastery }) => ({
    statLabel: 'Alle vakken beschikbaar',
    masteryPercent: overallMastery > 0 ? overallMastery : undefined,
  }),
}

export interface UseLabPersonalizationResult {
  isFluencyColdStart: boolean
  getEnrichmentForMode: (modeId: string) => ModeEnrichment
}

export function useLabPersonalization(
  personalRecords: PersonalRecords,
): UseLabPersonalizationResult {
  const { state, selectedGroup } = useAppStore()
  const groupSuffix = `_g${selectedGroup}`

  const fluencyFamilies = useMemo(() => {
    return Object.entries(state.familyMastery || {}).filter(
      ([key, s]) =>
        key.endsWith(groupSuffix) &&
        s.masteryScore >= 50 &&
        s.masteryScore < 80,
    )
  }, [state.familyMastery, groupSuffix])
  const fluencyCount = fluencyFamilies.length
  const fluencyMastery = useMemo(() => {
    if (fluencyCount === 0) return 0
    return Math.round(
      fluencyFamilies.reduce((acc, [_, s]) => acc + s.masteryScore, 0) /
        fluencyCount,
    )
  }, [fluencyFamilies, fluencyCount])

  const challengeFamilies = useMemo(() => {
    return Object.entries(state.familyMastery || {}).filter(
      ([key, s]) => key.endsWith(groupSuffix) && s.masteryScore >= 70,
    )
  }, [state.familyMastery, groupSuffix])
  const challengeCount = challengeFamilies.length
  const challengeMastery = useMemo(() => {
    if (challengeCount === 0) return 0
    return Math.round(
      challengeFamilies.reduce((acc, [_, s]) => acc + s.masteryScore, 0) /
        challengeCount,
    )
  }, [challengeFamilies, challengeCount])

  const weakestFamilyData = useMemo(() => {
    const weakest = getWeakestFamiliesGlobal(
      state.familyMastery || {},
      selectedGroup,
      1,
    )
    return weakest.length > 0 ? weakest[0] : null
  }, [state.familyMastery, selectedGroup])

  const overallMastery = useMemo(() => {
    const families = Object.entries(state.familyMastery || {}).filter(
      ([key, s]) => key.endsWith(groupSuffix) && s.attempts > 0,
    )
    if (families.length === 0) return 0
    const sum = families.reduce((acc, [_, s]) => acc + s.masteryScore, 0)
    return Math.round(sum / families.length)
  }, [state.familyMastery, groupSuffix])

  const isFluencyColdStart = useMemo(
    () => checkFluencyColdStart(state.familyMastery || {}, selectedGroup),
    [state.familyMastery, selectedGroup],
  )

  const getEnrichmentForMode = (modeId: string): ModeEnrichment => {
    const fn = MODE_ENRICHMENT[modeId as ModeId]
    if (!fn) return {}
    return fn({
      fluencyCount,
      fluencyMastery,
      challengeCount,
      challengeMastery,
      weakestFamilyData,
      overallMastery,
      personalRecords,
    })
  }

  return {
    isFluencyColdStart,
    getEnrichmentForMode,
  }
}
